import { useId } from "react";
import type { UiLang } from "../lib/types";

/**
 * Flaggene i språkvelgeren, tegnet framfor skrevet.
 *
 * De sto som emoji — 🇳🇴, 🇬🇧, 🇩🇪 — og det virker på Mac, iOS og Android. På
 * Windows gjør det ikke det: Segoe UI Emoji har ingen flaggglyfer, så
 * nettleseren faller tilbake til de to bokstavene regionalindikatorparet er
 * bygget av og tegner «NO», «GB», «DE» i små bokser. Det er ikke en manglende
 * font brukeren kan installere seg ut av — Microsoft har aldri levert
 * landflagg — og Windows er den vanligste maskinen appen åpnes på.
 *
 * SVG har ingen fontavhengighet og ser likt ut overalt. Fem flagg er få nok
 * til å tegne for hånd, så det koster heller ikke et ikonbibliotek.
 *
 * Alle fem er tegnet i samme 18×12-rute i stedet for hvert sitt offisielle
 * størrelsesforhold. Flaggene står ved siden av hverandre i en rad, og en rad
 * der Storbritannia er bredere enn Tyskland ser ut som en feil — selv når det
 * er de ekte proporsjonene.
 */

interface Props {
  lang: UiLang;
}

export function FlagIcon({ lang }: Props) {
  /*
   * Union Jack-flagget klipper de røde diagonalene mot en maske, og en maske
   * trenger en id. Alle fem flaggene står i DOM-en samtidig, og i utskrift og
   * i Roadbook kan velgeren stå to steder på siden — derfor useId og ikke en
   * fast streng.
   */
  const clipId = useId();

  return (
    <svg
      className="flag"
      viewBox="0 0 18 12"
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      {lang === "no" && (
        <>
          <rect width="18" height="12" fill="#BA0C2F" />
          <path d="M4 0h4v12H4z M0 4h18v4H0z" fill="#fff" />
          <path d="M5 0h2v12H5z M0 5h18v2H0z" fill="#00205B" />
        </>
      )}

      {lang === "en" && (
        <>
          <clipPath id={clipId}>
            {/* Motvendingen: bare de fire kvadrantene den røde diagonalen skal
                ligge i. Uten den blir Union Jack symmetrisk, og det er den ikke. */}
            <path d="M9 6h9v6z v6h-9z h-9v-6z v-6h9z" />
          </clipPath>
          <rect width="18" height="12" fill="#012169" />
          <path d="M0 0 18 12 M18 0 0 12" stroke="#fff" strokeWidth="2.4" />
          <path
            d="M0 0 18 12 M18 0 0 12"
            stroke="#C8102E"
            strokeWidth="1.6"
            clipPath={`url(#${clipId})`}
          />
          <path d="M9 0v12 M0 6h18" stroke="#fff" strokeWidth="4" />
          <path d="M9 0v12 M0 6h18" stroke="#C8102E" strokeWidth="2.4" />
        </>
      )}

      {lang === "de" && (
        <>
          <rect width="18" height="4" fill="#000" />
          <rect y="4" width="18" height="4" fill="#DD0000" />
          <rect y="8" width="18" height="4" fill="#FFCE00" />
        </>
      )}

      {lang === "fr" && (
        <>
          <rect width="6" height="12" fill="#002395" />
          <rect x="6" width="6" height="12" fill="#fff" />
          <rect x="12" width="6" height="12" fill="#ED2939" />
        </>
      )}

      {lang === "es" && (
        <>
          <rect width="18" height="12" fill="#AA151B" />
          <rect y="3" width="18" height="6" fill="#F1BF00" />
        </>
      )}
    </svg>
  );
}
